import React from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export type MediaKitMonthOption = { value: string; label: string };

export type MediaKitMonthsPickerProps = {
  options: MediaKitMonthOption[];
  value: string[];
  onChange: (next: string[]) => void;
};

function yearOf(value: string): string {
  return value.slice(0, 4);
}

function shortMonthLabel(label: string): string {
  return label.split(" ")[0] ?? label;
}

export const MediaKitMonthsPicker: React.FC<MediaKitMonthsPickerProps> = ({
  options,
  value,
  onChange,
}) => {
  const { t } = useTranslation("mediaKit");
  const selected = new Set(value);
  const byYear = React.useMemo(() => {
    const groups: Array<{ year: string; months: MediaKitMonthOption[] }> = [];
    for (const opt of options) {
      const y = yearOf(opt.value);
      const last = groups[groups.length - 1];
      if (last && last.year === y) last.months.push(opt);
      else groups.push({ year: y, months: [opt] });
    }
    return groups.reverse();
  }, [options]);

  const toggle = (month: string) => {
    onChange(
      selected.has(month)
        ? value.filter((m) => m !== month)
        : [...value, month].sort(),
    );
  };

  const latest = options[options.length - 1]?.value;
  const currentYear = latest ? yearOf(latest) : undefined;
  const quickActions: Array<{ label: string; months: string[] }> = latest
    ? [
        { label: t("currentMonth"), months: [latest] },
        {
          label: t("last3Months"),
          months: options.slice(-3).map((o) => o.value),
        },
        {
          label: t("fullYear"),
          months: options
            .filter((o) => yearOf(o.value) === currentYear)
            .map((o) => o.value),
        },
      ]
    : [];
  const sameSelection = (months: string[]) =>
    months.length === value.length && months.every((m) => selected.has(m));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {quickActions.map((qa) => (
          <Button
            key={qa.label}
            size="small"
            variant={sameSelection(qa.months) ? "contained" : "outlined"}
            disableElevation
            onClick={() => onChange(qa.months)}
            sx={{ textTransform: "none", borderRadius: 999 }}
          >
            {qa.label}
          </Button>
        ))}
      </Stack>
      {byYear.map(({ year, months }) => (
        <Box key={year}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: "block", mb: 0.75 }}
          >
            {year}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {months.map((m) => {
              const isOn = selected.has(m.value);
              return (
                <Chip
                  key={m.value}
                  label={shortMonthLabel(m.label)}
                  icon={isOn ? <CheckIcon /> : undefined}
                  color={isOn ? "primary" : "default"}
                  variant={isOn ? "filled" : "outlined"}
                  onClick={() => toggle(m.value)}
                  size="small"
                />
              );
            })}
          </Stack>
        </Box>
      ))}
      {value.length === 0 ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          {t("selectMonth")}
        </Alert>
      ) : null}
    </Stack>
  );
};
